import * as z from 'zod';
import { idempotencyKeySchema, isoDateSchema, isoDateTimeSchema, recordActionMethodSchema, recordedRefusalSchema, shortRefSchema, strict, uuidSchema, } from './common.js';
/**
 * The counter: one breakfast, one lunch and one dinner per person per day.
 *
 * This domain is the gate's sibling and is shaped like it on purpose, because it is the same act
 * performed for a different reason: a band is read, a person is identified, and one thing is
 * recorded against an append-only table. What differs is what "already done" means. A movement can
 * repeat all day and only a CONTRADICTION is an anomaly; a meal is an allowance, so the second one
 * of the same sitting is the thing the product exists to stop.
 *
 * **The allowance is enforced by a partial unique index, not by a read.** `meal_events` carries a
 * unique index on `(person_id, service_date, meal) where result = 'served'`, so two counters cannot
 * both feed one person by racing. A second serving is `served_again`, which sits outside that
 * predicate and may therefore repeat, and it is the reason this domain has reason codes at all.
 *
 * **The day is the civil day in the installation's reporting timezone, decided by the SERVER at
 * write time and then stored.** It is a `date` rather than an instant: "which day does this meal
 * count against" is a question with a calendar answer, and recomputing it later from `occurred_at`
 * would let a change of reporting timezone silently move meals between days. Nothing in this file
 * lets a client name it.
 *
 * **There is no note and no free text anywhere in this domain, deliberately.** An override's reason
 * is one of three closed tokens, each self-explanatory, so there is no prose about a named person to
 * seal under their key, no ciphertext column, no verb that opens one and no screen that reads one
 * back. The presence domain needs all of that because a movement's reason can be `other`; a second
 * helping cannot be, and not creating the prose is cheaper and safer than creating it and then
 * building somewhere to read it.
 *
 * **Nothing here is a second answer to a question the gate already answers.** The person, their
 * status, their photograph and their presence state arrive with the scan, through
 * `resolveScanResponseSchema`, which is where {@link diningStateSchema} rides too.
 */
/** The three sittings, in the order a day runs and in the order a screen draws them. */
export const MEALS = ['breakfast', 'lunch', 'dinner'];
export const mealSchema = z.enum(MEALS);
/**
 * What was recorded.
 *
 * `served` is the allowance being taken and is the only value the daily unique index covers.
 * `served_again` is an operator authorising a further helping, which is countable precisely because
 * it is its own value rather than a flag on `served`. `rejected` recorded an attempt that fed
 * nobody, which today is an archived record and nothing else.
 */
export const MEAL_RESULTS = ['served', 'served_again', 'rejected'];
export const mealResultSchema = z.enum(MEAL_RESULTS);
/**
 * Why a second helping was authorised. Three tokens, and the ones that are ABSENT are the design.
 *
 * There is no `other`, which every other closed reason list in this product carries, and there is
 * therefore no note: `staff_authorised` is the open answer, it is honest, and it means what an
 * unexplained `other` would have meant anyway. That is what keeps this domain free of prose about a
 * named person.
 *
 * There is also no `dietary_or_medical`. A second helping on medical grounds is real, and the
 * answer is still `staff_authorised`: recording WHY would put a health inference about a named
 * person on a row that can never be amended or deleted, which is special-category data this product
 * has no business holding to run a canteen. `docs/compliance/dpia-notes.md` records that decision.
 */
export const MEAL_REASON_CODES = ['meal_spoiled', 'recorded_in_error', 'staff_authorised'];
export const mealReasonCodeSchema = z.enum(MEAL_REASON_CODES);
/**
 * What one sitting looks like for one person today.
 *
 * `servings` counts `served` plus every `served_again`, so it is one where the allowance was taken
 * normally and two or more where somebody authorised a further helping. `served` is the boolean the
 * button reads, and it is kept beside the count rather than derived from it at the call site,
 * because "has this person had lunch" is the question the counter asks and a client should not have
 * to know that zero means no.
 */
export const mealStateSchema = strict({
    meal: mealSchema,
    served: z.boolean(),
    /** When the allowance was taken, or `null` when it has not been. */
    at: isoDateTimeSchema.nullable(),
    servings: z.int().nonnegative(),
});
/**
 * The counter's whole answer about one person, carried by the scan that identified them.
 *
 * **`suggested` is computed by the SERVER and that is not a convenience.** Which sitting is the
 * relevant one depends on the wall clock in the installation's reporting timezone, and a browser
 * computing it would be computing it in whatever zone the terminal happens to be set to. One
 * answer, from the process that already knows the zone.
 *
 * **`permittedMeals` is derived by asking the decision function**, exactly as `permittedActions` is,
 * so the screen never offers a button the submission would refuse and there is no second copy of
 * the matrix. It is empty for an archived record.
 *
 * The three states are an ARRAY in `MEALS` order rather than a record keyed by meal, so the order a
 * screen draws them in is the product's rather than an object literal's.
 */
export const diningStateSchema = strict({
    /** The civil day these three states belong to, in the reporting timezone. */
    serviceDate: isoDateSchema,
    suggested: mealSchema,
    permittedMeals: z.array(mealSchema).max(MEALS.length),
    meals: z.array(mealStateSchema).length(MEALS.length),
});
/**
 * Serving one meal.
 *
 * **There is no `idempotencyKey` minted for this route and no `serviceDate` on the wire.** The key
 * is the one `POST /api/v1/presence/resolve` already minted for the scan: `presence_events` and
 * `meal_events` carry their own partial unique indexes, so one scan's key is spent once in each and
 * neither lookup can see the other, which makes a second key a second thing to keep straight for a
 * guarantee the tables already give. The service date is the server's, for the reason
 * {@link diningStateSchema} gives.
 *
 * `method` is the five an operator at a terminal produces and nothing else. The wider
 * `presenceMethodSchema` carries four machine values; none of them has a route here, because no API
 * key reaches this one.
 */
export const submitMealRequestSchema = strict({
    personId: uuidSchema,
    /**
     * The band that was presented, or `null`.
     *
     * Nullable rather than optional, exactly as the movement's is: somebody found by a manual search,
     * or holding no band at all, is a legitimate serving, and requiring the member to be PRESENT is
     * what makes "this meal named no pass" something a client states rather than forgets.
     */
    credentialId: uuidSchema.nullable(),
    meal: mealSchema,
    method: recordActionMethodSchema,
    idempotencyKey: idempotencyKeySchema,
    /** Required when the allowance has already been taken, and only then. */
    reasonCode: mealReasonCodeSchema.optional(),
    clientLabel: z.string().max(64).optional(),
});
/** One row of the meal record, as a client reads it back. */
export const mealEventSchema = strict({
    id: uuidSchema,
    personId: uuidSchema,
    credentialId: uuidSchema.nullable(),
    meal: mealSchema,
    serviceDate: isoDateSchema,
    result: mealResultSchema,
    reasonCode: mealReasonCodeSchema.nullable(),
    method: recordActionMethodSchema,
    operatorId: uuidSchema.nullable(),
    occurredAt: isoDateTimeSchema,
    clientLabel: z.string().nullable(),
});
/**
 * The answer to a serving.
 *
 * **`dining` is the whole state again, not a delta**, and it is what makes the panel that stays on
 * screen correct: without it the button an operator just pressed would go on saying the meal had
 * not been taken, directly beneath the confirmation that it had. It is the same shape the scan
 * carried, so a client replaces rather than merges.
 *
 * `refusal` is null for a serving that happened. It exists for the same reason the movement's does:
 * a refused MEAL is a recorded row and an explanation, while a refused REQUEST is the error
 * contract and writes nothing.
 */
export const submitMealResponseSchema = strict({
    event: mealEventSchema,
    /** The key had already produced a serving, and that serving is what came back. */
    replayed: z.boolean(),
    dining: diningStateSchema,
    refusal: recordedRefusalSchema.nullable(),
});
/**
 * Which rows of the roster to return.
 *
 * `incomplete` is fewer than three; `none` is nobody fed at all. The narrowing goes into the query
 * rather than onto what comes back, and the totals beside the list always describe the WHOLE
 * eligible population whatever the filter says, so switching the filter never moves a figure.
 */
export const DINING_ROSTER_FILTERS = ['all', 'incomplete', 'none'];
export const diningRosterFilterSchema = z.enum(DINING_ROSTER_FILTERS);
export const diningRosterQuerySchema = strict({
    /** The civil day to report on. The server defaults it to the service date now. */
    date: isoDateSchema.optional(),
    filter: diningRosterFilterSchema.default('all'),
});
/**
 * One line of the roster.
 *
 * The shape is the roll-call's four facts plus the three sittings, and `name` is nullable for the
 * reason the roll-call's is: the projection excludes an erased person and a build that is still
 * running excludes everybody, and dropping such a row would produce a list short by exactly the
 * people nobody can identify. The `shortRef` is always there and is quotable.
 */
export const diningRosterEntrySchema = strict({
    personId: uuidSchema,
    shortRef: shortRefSchema,
    name: z.string().nullable(),
    imageId: uuidSchema.nullable(),
    meals: z.array(mealStateSchema).length(MEALS.length),
    /** How many of the three were taken, so a client sorts and filters without counting. */
    mealsTaken: z.int().min(0).max(MEALS.length),
});
/**
 * Who has eaten and who has not, for one civil day.
 *
 * **It never pages, and the roll-call's reason for not paging is NOT the reason.** That one is a
 * physical-safety argument — paging a sheet somebody reads aloud outside a burning building is how
 * a name is missed — and it does not transfer to a meal roster, which nobody evacuates with. Two
 * arguments of its own carry it instead. The first is SIZE: this list is the eligible residence
 * population, filtered exactly as `presence.rollcall`'s is, so it is the same length as a response
 * this product already returns whole, and its ceiling is the building rather than a guess about
 * production. The second is PURPOSE: the question is "who has had nothing today", and a first page
 * of an unread list is the wrong half of the answer.
 *
 * `entries` therefore carries no `.max()`, deliberately, because a ceiling the server can exceed
 * is a blank panel rather than a refused request — the browser `safeParse`s every answer. The day
 * this facility is large enough for that to be wrong, the fix is `{ items, total }` and a cap
 * enforced in SQL, not a bound asserted here and hoped for.
 *
 * It is ordered by meals taken ASCENDING and then by `shortRef`, so the people to chase are the
 * first lines.
 *
 * **The totals are computed from the same rows the list is**, in one statement, so the figure and
 * the evidence for it cannot disagree; and they describe the whole eligible population rather than
 * the filtered slice, so a filter never moves a number.
 */
export const diningRosterTotalsSchema = strict({
    /** The eligible population: not erased, not archived. The same filter the occupancy counts use. */
    people: z.int().nonnegative(),
    breakfast: z.int().nonnegative(),
    lunch: z.int().nonnegative(),
    dinner: z.int().nonnegative(),
    /** How many took all three, exactly two, exactly one, and nothing at all. */
    all: z.int().nonnegative(),
    two: z.int().nonnegative(),
    one: z.int().nonnegative(),
    none: z.int().nonnegative(),
});
export const diningRosterSchema = strict({
    serviceDate: isoDateSchema,
    generatedAt: isoDateTimeSchema,
    filter: diningRosterFilterSchema,
    /** False when somebody on the list could not be named, exactly as on the roll-call. */
    complete: z.boolean(),
    totals: diningRosterTotalsSchema,
    entries: z.array(diningRosterEntrySchema),
});
//# sourceMappingURL=dining.js.map